// Funds

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import { CompanyFund, FundBody } from './types';

export const getCompanyFunds = (dispatch: Dispatch) =>
  useQuery({
    queryKey: ['funds'],
    queryFn: async (): Promise<CompanyFund[] | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/fund`,
        dispatch,
      });
      if (response) return response.data.items;
    },
    staleTime: 1000 * 60 * 60 * 12,
    gcTime: 1000 * 60 * 60 * 12,
    retry: 1,
  });

export const createFund = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['funds', 'create'],
    mutationFn: async (data: FundBody) => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/fund`,
        data,
      });
      if (response) return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['funds'] });
      showAlert(dispatch, 'success', 'Fondo creado con éxito');
    },
    onError: (error) => errorHandler(dispatch, error),
  });
};

export const updateFund = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['funds', 'update'],
    mutationFn: async (data: FundBody) => {
      const response = await requestHandler({
        method: 'patch',
        url: `internal/fund/${data.id}`,
        data,
        dispatch,
      });
      if (response) return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['funds'] });
      showAlert(dispatch, 'success', 'Fondo actualizado con éxito');
    },
    onError: (error) => errorHandler(dispatch, error),
  });
};

export const selectFundAsDefault = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['funds', 'selectAsDefault'],
    mutationFn: async (id: string) => {
      const response = await requestHandler({
        method: 'patch',
        url: `internal/fund/${id}/selectAsDefault`,
        dispatch,
      });
      if (response) return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['funds'] });
      showAlert(
        dispatch,
        'success',
        'Fondo seleccionado como principal con éxito',
      );
    },
    onError: (error) => errorHandler(dispatch, error),
  });
};

export const getAllFundPayments = async (dispatch: Dispatch, query: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/fund/payments?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const revertFundPayment = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/fund/payments/${data.id}/revert`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const checkRevertFundPayment = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/fund/payments/${data.id}/revert/check`,
    dispatch,
  });
  if (response) return response.data;
};

export const checkResellReversal = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/fund/${data.id}/revert/check`,
    dispatch,
  });
  if (response) return response.data;
};

export const revertResell = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/fund/${data.id}/revert`,
    dispatch,
  });
  if (response) return response.data;
};

export const createFundPayments = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/fund/payments`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const createFundInvoicesBuyBack = async (
  dispatch: Dispatch,
  data: {
    resellsForBuyback: { id: number; amount: number; buybackAt: Date }[];
  },
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/fund/buy-back`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const createFundOwnCollection = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/fund/own-collection`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const updateFundPayment = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/fund/${data.id}/payments/${data.paymentId}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringResellsArrear = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/fund/documents/arrears`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const createFactoringResellPendingAccounts = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/fund/documents/pending-accounts`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const checkExistingCollections = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/fund/payments/check-collections`,
    data,
    dispatch,
  });
  if (response) return response.data;
};
